import React, { useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  Alert, AlertIcon, FormControl, FormLabel, Input, Select, Button, HStack, Text, Link, useToast,
} from '@chakra-ui/react';
import { FiExternalLink } from 'react-icons/fi';
import API from '../../helpers/axiosInstance';
import { getApiHeaders } from '../../utils/DefaultFunction';
import { BRAND_NAME } from '../../theme/brand';

/**
 * MixpanelModal — connect a tenant's Mixpanel project (API Secret / Service Account)
 * and import events for a date range into the event store. Mirrors the CRM
 * token-connect + sync flow; tenant is pinned server-side (RULE-01).
 */
export default function MixpanelModal({ isOpen, onClose, connected, onChanged }) {
  const toast = useToast();
  const [secret, setSecret] = useState('');
  const [project, setProject] = useState('');
  const [residency, setResidency] = useState('us');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    if (!secret.trim()) { toast({ title: 'API Secret is required', status: 'warning', duration: 3000 }); return; }
    setBusy(true);
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/integration/mixpanel/connect`,
        { api_secret: secret.trim(), project_id: project.trim(), residency }, { headers: getApiHeaders() });
      toast({ title: 'Mixpanel connected', status: 'success', duration: 3000, isClosable: true });
      setSecret('');
      onChanged && onChanged();
    } catch (e) {
      toast({ title: e?.response?.data?.message || 'Could not connect Mixpanel', status: 'error', duration: 4000, isClosable: true });
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    if (!from || !to) { toast({ title: 'Pick a from + to date', status: 'warning', duration: 3000 }); return; }
    setBusy(true);
    toast({ title: 'Importing Mixpanel events… this can take a moment', status: 'info', duration: 2500 });
    try {
      const r = await API.post(`${process.env.REACT_APP_API_URL}/integration/mixpanel/import`,
        { from, to }, { headers: getApiHeaders() });
      const s = r.data?.data?.stats || {};
      toast({
        title: 'Import complete',
        description: `${s.inserted || 0} events loaded · ${s.skipped || 0} skipped (no identity)`,
        status: 'success', duration: 7000, isClosable: true,
      });
      onChanged && onChanged();
    } catch (e) {
      toast({ title: e?.response?.data?.message || 'Import failed', status: 'error', duration: 5000, isClosable: true });
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    try {
      await API.post(`${process.env.REACT_APP_API_URL}/integration/mixpanel/disconnect`, {}, { headers: getApiHeaders() });
      onChanged && onChanged();
      onClose();
    } catch (e) { /* noop */ }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Mixpanel</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {!connected ? (
            <>
              <Alert status="info" borderRadius="md" fontSize="sm" mb={3}>
                <AlertIcon />Import your Mixpanel events into {BRAND_NAME}. Get the API Secret from Mixpanel → Project Settings.
              </Alert>
              <FormControl mb={3} isRequired>
                <FormLabel fontSize="sm" mb={1}>API Secret</FormLabel>
                <Input size="sm" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Mixpanel project API Secret" />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel fontSize="sm" mb={1}>Project ID</FormLabel>
                <Input size="sm" value={project} onChange={(e) => setProject(e.target.value)} placeholder="optional (required for Service Accounts)" />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel fontSize="sm" mb={1}>Data residency</FormLabel>
                <Select size="sm" value={residency} onChange={(e) => setResidency(e.target.value)}>
                  <option value="us">US (data.mixpanel.com)</option>
                  <option value="eu">EU (data-eu.mixpanel.com)</option>
                  <option value="in">India (data-in.mixpanel.com)</option>
                </Select>
              </FormControl>
              <Button w="full" bg="#7856ff" color="white" _hover={{ bg: '#6a45ff' }} isLoading={busy} onClick={connect}>
                Connect Mixpanel
              </Button>
              <Link href="https://developer.mixpanel.com/reference/raw-event-export" isExternal fontSize="xs" color="var(--brand-primary)" mt={2} display="inline-block">
                Mixpanel export docs <FiExternalLink style={{ display: 'inline', verticalAlign: '-1px' }} />
              </Link>
            </>
          ) : (
            <>
              <Alert status="success" borderRadius="md" fontSize="sm" mb={3}>
                <AlertIcon />Mixpanel connected. Pick a date range and import events.
              </Alert>
              <HStack mb={3} align="end">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" mb={1}>From</FormLabel>
                  <Input size="sm" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" mb={1}>To</FormLabel>
                  <Input size="sm" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </FormControl>
              </HStack>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Events are matched to contacts by phone/email; anonymous (distinct_id-only) events are imported for analytics but won't tie to a contact.
              </Text>
              <Button w="full" bg="#7856ff" color="white" _hover={{ bg: '#6a45ff' }} isLoading={busy} onClick={runImport}>
                Import events
              </Button>
              <Button w="full" mt={2} size="sm" variant="outline" colorScheme="red" onClick={disconnect}>
                Disconnect
              </Button>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
